import AuthLayout from '@/app/layouts/AuthLayout';
import { webauthnErrorMessage } from '@/shared/lib/webauthn-error';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { InputError } from '@/shared/ui/input-error';
import { Label } from '@/shared/ui/label';
import { OtpCodeInput } from '@/shared/ui/otp-code-input';
import { PasswordInput } from '@/shared/ui/password-input';
import { Separator } from '@/shared/ui/separator';
import { TextLink } from '@/shared/ui/text-link';
import { Head, router, useForm, usePage } from '@inertiajs/react';
import { startAuthentication } from '@simplewebauthn/browser';
import { Fingerprint, LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
    status?: string;
    error?: string;
    canResetPassword: boolean;
    otp_login_auto_detect_client?: boolean;
}

interface FlashShape {
    /** Server-side passkey rejection (spatie's `authenticatePasskey::message`) or any other flashed error. */
    error?: string;
    mfa?: { otp_clients?: Array<{ id: number; name: string }> };
    require_otp?: boolean;
    data?: unknown;
}

export default function Login({ status, error, otp_login_auto_detect_client = true }: Props) {
    const page = usePage<{ flash?: FlashShape; seo: { page_title: string | null } }>();
    const flash = page.props.flash ?? {};

    const form = useForm({
        a_code: '',
        password: '',
        password_hint: '',
        remember: false,
    });

    const otpForm = useForm<{ client_id: string; otp_code: string }>({
        client_id: '',
        otp_code: '',
    });

    const [showOtpFallback, setShowOtpFallback] = useState(false);
    // The passkey challenge stays available after the auto-prompt so the user can
    // re-trigger it from a button if the native dialog was dismissed or failed
    // (a common, confusing dead-end on Windows).
    const [passkeyChallenge, setPasskeyChallenge] = useState<unknown>(null);
    // The server consumes its copy of the challenge on the first POST, so a retry
    // after a *server-side* rejection has to fetch a fresh one (see withPassKey).
    const [passkeyChallengeConsumed, setPasskeyChallengeConsumed] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);

    const otpClients = useMemo(() => flash.mfa?.otp_clients ?? [], [flash.mfa?.otp_clients]);
    const shouldAutoDetectOtpClient = otp_login_auto_detect_client !== false;

    function withPassKey(userOptions?: unknown) {
        setPasskeyBusy(true);
        (async () => {
            try {
                let options: unknown;
                if (typeof userOptions === 'object' && userOptions !== null && 'allowCredentials' in (userOptions as Record<string, unknown>)) {
                    options = userOptions;
                } else {
                    const response = await fetch(route('passkeys.authentication_options'));
                    options = await response.json();
                }
                const startAuthenticationResponse = await startAuthentication({
                    optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
                });
                router.post(
                    route('passkeys.login'),
                    {
                        start_authentication_response: JSON.stringify(startAuthenticationResponse),
                    },
                    {
                        onFinish: () => setPasskeyChallengeConsumed(true),
                    },
                );
            } catch (err) {
                toast.error(webauthnErrorMessage(err, 'login'));
            } finally {
                setPasskeyBusy(false);
            }
        })();
    }

    /* eslint-disable react-hooks/set-state-in-effect */
    useEffect(() => {
        if (!shouldAutoDetectOtpClient && otpClients.length > 0 && !otpForm.data.client_id) {
            otpForm.setData('client_id', String(otpClients[0].id));
        }

        const errors = form.errors as Record<string, string | boolean | undefined>;
        if (errors.require_otp || flash.require_otp) {
            setShowOtpFallback(true);
        }
        if (errors.require_passkey) {
            const passkeyData = typeof flash.data === 'string' ? JSON.parse(flash.data) : flash.data;
            setPasskeyChallenge(passkeyData);
            setPasskeyChallengeConsumed(false);
            withPassKey(passkeyData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.errors, flash.mfa, flash.require_otp]);
    /* eslint-enable react-hooks/set-state-in-effect */

    function submit(e: React.FormEvent) {
        e.preventDefault();
        form.post(route('login'), {
            onFinish: () => form.reset('password'),
        });
    }

    function submitOtp() {
        otpForm.transform((data) => ({
            otp_code: data.otp_code,
            ...(shouldAutoDetectOtpClient ? {} : { client_id: data.client_id }),
        }));
        otpForm.post(route('login.otp.verify'), {
            preserveState: true,
            onFinish: () => {
                otpForm.reset('otp_code');
                otpForm.transform((data) => data);
            },
        });
    }

    return (
        <AuthLayout title="Увійдіть до свого акаунта" description="Введіть свій код пілота та пароль для входу" cardContentClass="max-w-sm">
            {/* Title text comes from config/seo.php via HandleSeo, not a literal:
                Inertia rewrites <title> on hydration, so the client must rebuild
                exactly what the server rendered or Googlebot indexes a different
                title than the one served. app.tsx appends " - {appName}". */}
            <Head title={page.props.seo.page_title ?? undefined} />

            {status && <div className="mb-4 text-center text-sm font-medium text-green-600">{status}</div>}
            {error && <div className="mb-4 text-center text-sm font-medium text-destructive">{error}</div>}
            {/* A passkey the server turned down (bad signature, stale challenge, counter…)
                lands here as flash.error. Without it the page just reloaded silently. */}
            {flash.error && !error && (
                <div role="alert" className="mb-4 text-center text-sm font-medium text-destructive">
                    {flash.error}
                </div>
            )}

            <form onSubmit={submit} className="flex flex-col gap-6" method="post" name="signin">
                <div className="grid gap-6">
                    <section className="grid gap-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="username">Код пілота</Label>
                            <TextLink
                                href={route('password.request')}
                                className="text-sm font-medium underline underline-offset-4 hover:text-primary"
                            >
                                Забули код пілота?
                            </TextLink>
                        </div>
                        <Input
                            id="username"
                            type="text"
                            required
                            autoFocus
                            tabIndex={1}
                            value={form.data.a_code}
                            onChange={(e) => form.setData('a_code', e.target.value)}
                            placeholder="Код пілота"
                            name="a_code"
                            autoComplete="username"
                        />
                        <InputError message={form.errors.a_code} />
                    </section>

                    <section className="grid gap-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="password">Пароль</Label>
                            <TextLink
                                href={route('password.request', { a_code: form.data.a_code })}
                                className="text-sm font-medium underline underline-offset-4 hover:text-primary"
                            >
                                Забули пароль?
                            </TextLink>
                        </div>
                        <PasswordInput
                            id="password"
                            required
                            tabIndex={2}
                            autoComplete="current-password"
                            value={form.data.password}
                            onChange={(e) => form.setData('password', e.target.value)}
                            placeholder="Password"
                            name="password"
                        />
                        {form.errors.password_hint && <InputError message={`Підказка: ${form.errors.password_hint}`} />}
                    </section>
                    <input type="text" name="nickname" className="hidden" autoComplete="off" />

                    <Button type="submit" className="w-full" tabIndex={4} disabled={form.processing}>
                        {form.processing && <LoaderCircle className="size-4 animate-spin" />}
                        Авторизуватися
                    </Button>
                </div>
            </form>

            {passkeyChallenge != null && (
                <div className="grid gap-3">
                    <div className="grid gap-2 rounded-lg border p-3 text-center">
                        <p className="text-sm text-muted-foreground">
                            Підтвердьте вхід ключем доступу (біометрія, PIN або апаратний ключ). Якщо вікно не зʼявилося або ви його закрили —
                            натисніть кнопку нижче.
                        </p>
                        <Button
                            type="button"
                            variant="secondary"
                            className="w-full"
                            onClick={() => withPassKey(passkeyChallengeConsumed ? undefined : passkeyChallenge)}
                            disabled={passkeyBusy}
                        >
                            {passkeyBusy ? <LoaderCircle className="size-4 animate-spin" /> : <Fingerprint className="size-4" />}
                            Увійти через ключ доступу
                        </Button>
                    </div>
                    {otpClients.length > 0 && (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowOtpFallback(true)}
                                className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary"
                            >
                                Не вдається скористатися ключем? Увійти через код
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="text-center text-sm text-muted-foreground">
                Немає акаунту?{' '}
                <TextLink href={route('register')} className="underline underline-offset-4" tabIndex={6}>
                    Зареєструватися
                </TextLink>
            </div>

            <Separator className="my-1" />
            <div className="px-2 text-center text-sm text-muted-foreground">Стара версія Milpilots відключена</div>

            <Dialog open={showOtpFallback} onOpenChange={setShowOtpFallback}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Вхід через OTP</DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-4">
                        {!shouldAutoDetectOtpClient && (
                            <div className="grid gap-2">
                                <Label htmlFor="otp_client_id">OTP клієнт</Label>
                                <select
                                    id="otp_client_id"
                                    value={otpForm.data.client_id}
                                    onChange={(e) => otpForm.setData('client_id', e.target.value)}
                                    className="flex h-10 w-full rounded-md border px-3 text-sm"
                                >
                                    {otpClients.map((client) => (
                                        <option key={client.id} value={String(client.id)}>
                                            {client.name}
                                        </option>
                                    ))}
                                </select>
                                <InputError message={otpForm.errors.client_id} />
                            </div>
                        )}

                        <div className="grid justify-items-center gap-2">
                            <OtpCodeInput
                                id="otp_code"
                                value={otpForm.data.otp_code}
                                onChange={(value) => otpForm.setData('otp_code', value)}
                                size="lg"
                                length={6}
                                disabled={otpForm.processing}
                            />
                            <InputError message={otpForm.errors.otp_code || (form.errors as Record<string, string>).otp_code} />
                        </div>

                        <Button
                            type="button"
                            disabled={otpForm.processing || (!shouldAutoDetectOtpClient && !otpForm.data.client_id)}
                            onClick={submitOtp}
                        >
                            {otpForm.processing && <LoaderCircle className="size-4 animate-spin" />}
                            Підтвердити OTP
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </AuthLayout>
    );
}
